// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
export const GLOSSARY_CATEGORIES = [
    { id: "budget-shopping", title: { en: "Budget & shopping", de: "Budget & Einkauf" }, populated: true },
    { id: "nutrition-targets", title: { en: "Nutrition & targets", de: "Ernährung & Ziele" }, populated: false },
    { id: "plans-meals", title: { en: "Plans & meals", de: "Pläne & Mahlzeiten" }, populated: false },
    { id: "pantry", title: { en: "Kitchen", de: "Küche" }, populated: false },
    { id: "amy-personalization", title: { en: "Amy & personalization", de: "Amy & Personalisierung" }, populated: false },
    { id: "progress", title: { en: "Progress", de: "Fortschritt" }, populated: false },
];
export const GLOSSARY_KIND_LABEL = {
    target: { en: "Your goal", de: "Dein Ziel" },
    constraint: { en: "Your limit", de: "Deine Grenze" },
    estimate: { en: "Estimate", de: "Schätzung" },
    forecast: { en: "Forecast", de: "Prognose" },
    actual: { en: "Actual", de: "Tatsächlich" },
    info: { en: "Good to know", de: "Gut zu wissen" },
};
export const GLOSSARY_UI = {
    en: {
        title: "Glossary",
        subtitle: "The words BiteBalance uses for your budget, food and progress — in plain language.",
        comingSoon: "Coming soon.",
        learnMore: "Learn more",
        whatDoesMean: "What does {{name}} mean?",
        example: "For example",
        showMore: "Show more",
        showLess: "Show less",
        close: "Close",
        notTrackedYet: "Not tracked yet",
    },
    de: {
        title: "Glossar",
        subtitle: "Die Begriffe, die BiteBalance für dein Budget, dein Essen und deinen Fortschritt verwendet — einfach erklärt.",
        comingSoon: "Folgt bald.",
        learnMore: "Mehr erfahren",
        whatDoesMean: "Was bedeutet {{name}}?",
        example: "Zum Beispiel",
        showMore: "Mehr anzeigen",
        showLess: "Weniger anzeigen",
        close: "Schließen",
        notTrackedYet: "Noch nicht erfasst",
    },
};
export const GLOSSARY_TERMS = [
    {
        id: "weekly-spending-goal",
        category: "budget-shopping",
        kind: "target",
        text: {
            en: {
                name: "Weekly spending goal",
                shortDefinition: "The amount you'd like your food to average around per week. It's a planning goal, not a checkout limit.",
                explanation: "This is a goal, not a limit. BiteBalance plans your meals toward it while keeping your allergies, excluded foods and nutrition targets first: when a week comes out above your goal, it looks for a cheaper week that still meets them. A big stock-up shop can cost more than your goal in one week and less the next — that's normal, which is why BiteBalance compares your goal with your forecast weekly average rather than with a single shop.",
                example: "Goal €65 a week. This week's shop comes to €96 because you're stocking up, but your forecast weekly average is €61 — you're on track.",
            },
            de: {
                name: "Wöchentliches Ausgabenziel",
                shortDefinition: "Der Betrag, den deine Lebensmittel pro Woche im Schnitt etwa kosten sollen. Ein Planungsziel, keine Grenze an der Kasse.",
                explanation: "Das ist ein Ziel, keine Grenze. BiteBalance plant deine Mahlzeiten darauf hin und hält dabei deine Allergien, ausgeschlossenen Lebensmittel und Ernährungsziele ein: Liegt eine Woche über deinem Ziel, sucht es eine günstigere Woche, die diese Regeln weiterhin erfüllt. Ein großer Aufbau-Einkauf kann in einer Woche mehr kosten als dein Ziel und in der nächsten weniger. Das ist normal; deshalb vergleicht BiteBalance dein Ziel mit deinem voraussichtlichen Wochendurchschnitt und nicht mit einem einzelnen Einkauf.",
                example: "Ziel 65 € pro Woche. Der Einkauf dieser Woche kostet 96 €, weil du Vorräte anlegst. Dein voraussichtlicher Wochendurchschnitt liegt bei 61 €, du bist also im Ziel.",
            },
        },
    },
    {
        id: "maximum-checkout",
        category: "budget-shopping",
        kind: "constraint",
        text: {
            en: {
                name: "Maximum checkout",
                shortDefinition: "The most you're willing to pay for one shop. Unlike your weekly spending goal, BiteBalance treats it as a firm limit and tells you when no plan fits under it.",
                explanation: "Set this if there's a real limit to what you can put through the till in one go. BiteBalance treats it as a firm line: if it can't build a week whose shop stays under it, it tells you and keeps your current plan instead of pretending. Want to spend more sometimes? Raise or remove the maximum in Settings.",
                example: "Maximum €90. The cheapest week BiteBalance can build costs €96 at the till, so it tells you it's €6 over and leaves your plan as it is.",
            },
            de: {
                name: "Maximaler Einkaufsbetrag",
                shortDefinition: "Der höchste Betrag, den du für einen einzelnen Einkauf ausgeben willst. Anders als das wöchentliche Ausgabenziel ist er für BiteBalance eine feste Grenze: Passt kein Plan darunter, sagt es dir das.",
                explanation: "Setze ihn, wenn es eine echte Grenze dafür gibt, was du auf einmal an der Kasse ausgeben kannst. BiteBalance behandelt ihn als feste Linie: Wenn keine Woche gebaut werden kann, deren Einkauf darunter bleibt, sagt es dir das und behält deinen aktuellen Plan, statt etwas vorzutäuschen. Du möchtest manchmal mehr ausgeben? Erhöhe oder entferne das Maximum in den Einstellungen.",
                example: "Maximum 90 €. Die günstigste Woche, die BiteBalance bauen kann, kostet 96 € an der Kasse — es sagt dir, dass sie 6 € darüber liegt, und lässt deinen Plan, wie er ist.",
            },
        },
    },
    {
        id: "forecast-weekly-average",
        category: "budget-shopping",
        kind: "forecast",
        text: {
            en: {
                name: "Forecast weekly average",
                shortDefinition: "The estimated value of the food this week's plan uses, whether you buy it now or already have it at home. It's the number your goal is compared with, not what you pay at the next shop.",
                explanation: "It counts the food your meals use, including things you already have at home, and leaves out the part of full packs kept for later weeks. It's an estimate based on this week's plan and typical prices, not a record of what you spent — which is why BiteBalance compares it with your weekly spending goal instead of comparing the goal with a single shop.",
                example: "This week's shop is €96, but €35 of it is food for later weeks. Your forecast weekly average is about €61.",
            },
            de: {
                name: "Voraussichtlicher Wochendurchschnitt",
                shortDefinition: "Der geschätzte Wert der Lebensmittel, die der Plan dieser Woche verbraucht, egal ob du sie jetzt kaufst oder schon zu Hause hast. Mit dieser Zahl wird dein Ausgabenziel verglichen; sie ist nicht der Betrag an der Kasse.",
                explanation: "Sie zählt, was deine Mahlzeiten verbrauchen, auch das, was du schon zu Hause hast, und lässt den Teil ganzer Packungen weg, der für spätere Wochen bleibt. Es ist eine Schätzung auf Basis des Plans dieser Woche und üblicher Preise, keine Aufzeichnung deiner Ausgaben. Deshalb vergleicht BiteBalance diese Zahl mit deinem wöchentlichen Ausgabenziel und nicht einen einzelnen Einkauf.",
                example: "Der Einkauf dieser Woche kostet 96 €, aber 35 € davon sind Lebensmittel für spätere Wochen. Dein voraussichtlicher Wochendurchschnitt liegt bei etwa 61 €.",
            },
        },
    },
    {
        id: "actual-weekly-average",
        category: "budget-shopping",
        kind: "actual",
        text: {
            en: {
                name: "Actual weekly average",
                shortDefinition: "What you really spent on groceries per week. Not tracked yet.",
                explanation: "BiteBalance doesn't see your receipts or what you pay at the till, so it can't tell you your real spending — and it won't guess. Until you can record your shops, this stays \"Not tracked yet\". It's shown so you always know the difference between our estimate and your real spending.",
            },
            de: {
                name: "Tatsächlicher Wochendurchschnitt",
                shortDefinition: "Was du wirklich pro Woche für Lebensmittel ausgegeben hast. Noch nicht erfasst.",
                explanation: "BiteBalance sieht weder deine Kassenbons noch, was du an der Kasse bezahlst — deshalb kann es dir deine echten Ausgaben nicht nennen und rät auch nicht. Bis du deine Einkäufe erfassen kannst, bleibt es bei „Noch nicht erfasst“. Es wird angezeigt, damit du immer den Unterschied zwischen unserer Schätzung und deinen echten Ausgaben kennst.",
            },
        },
    },
    {
        id: "next-shop-estimate",
        category: "budget-shopping",
        kind: "estimate",
        text: {
            en: {
                name: "Next shop estimate",
                shortDefinition: "The estimated amount you may pay at the till for your next shopping list, buying the full packs you need. It covers only what you still need to buy.",
                explanation: "It's the amount you'd actually hand over at the checkout, so it's never hidden or spread across weeks. It can be higher than your forecast weekly average, because packs come in fixed sizes and part of what you buy is expected to be left for later weeks. Prices are typical store prices and may vary where you shop.",
                example: "28 items, all priced: about €96 at the till.",
            },
            de: {
                name: "Nächster Einkauf (geschätzt)",
                shortDefinition: "Der geschätzte Betrag, den du beim nächsten Einkauf an der Kasse zahlst, wenn du die nötigen ganzen Packungen kaufst. Er umfasst nur, was du noch kaufen musst.",
                explanation: "Das ist der Betrag, den du tatsächlich an der Kasse zahlen würdest; er wird nie versteckt oder auf mehrere Wochen verteilt. Er kann über deinem voraussichtlichen Wochendurchschnitt liegen, weil Packungen feste Größen haben und ein Teil davon voraussichtlich für spätere Wochen übrig bleibt. Die Preise sind übliche Ladenpreise und können je nach Markt abweichen.",
                example: "28 Artikel, alle mit Preis: etwa 96 € an der Kasse.",
            },
        },
    },
    {
        id: "pantry-carryover",
        category: "budget-shopping",
        kind: "estimate",
        text: {
            en: {
                name: "Left for your kitchen",
                shortDefinition: "The estimated value of what's left of the packs you buy after this week's meals take what they need. It may be available for later meals; it isn't a record of what's in your kitchen.",
                explanation: "When you buy a 1 kg pack but this week's meals only need 600 g, the rest is carryover. That's why a stock-up week costs more at the till than your forecast weekly average: you're paying now for food you may eat later. It's still money you spend, and it isn't a guarantee that next week will be cheaper.",
                example: "€35 of this €96 shop is expected to be left over for later weeks.",
            },
            de: {
                name: "Bleibt in deiner Küche",
                shortDefinition: "Der geschätzte Wert dessen, was von deinen gekauften Packungen übrig bleibt, nachdem die Mahlzeiten dieser Woche versorgt sind. Es kann für spätere Mahlzeiten zur Verfügung stehen, ist aber keine Aufzeichnung dessen, was wirklich in deiner Küche ist.",
                explanation: "Kaufst du eine 1-kg-Packung, brauchen die Mahlzeiten dieser Woche aber nur 600 g, bleibt der Rest in deiner Küche. Deshalb kostet eine Aufbauwoche an der Kasse mehr als dein voraussichtlicher Wochendurchschnitt: Du zahlst jetzt für Lebensmittel, die du vielleicht später isst. Es ist trotzdem Geld, das du ausgibst, und keine Garantie, dass die nächste Woche günstiger wird.",
                example: "35 € dieses Einkaufs von 96 € bleiben voraussichtlich für spätere Wochen übrig.",
            },
        },
    },
    {
        id: "food-consumed-this-week",
        category: "budget-shopping",
        kind: "estimate",
        text: {
            en: {
                name: "Food consumed this week",
                shortDefinition: "The estimated value of the part of this shop that this week's planned meals use. It doesn't mean you've eaten it yet, and it leaves out the part of each pack kept for later.",
                explanation: "Together with what is left for your kitchen it explains your whole shop: everything you buy is either used by this week's meals or left for later. It only counts food you buy in this shop; food you already had at home is part of your forecast weekly average instead.",
                example: "€61 of this €96 shop is food consumed this week; the rest is left for your kitchen.",
            },
            de: {
                name: "Lebensmittelverbrauch dieser Woche",
                shortDefinition: "Der geschätzte Wert des Teils dieses Einkaufs, den die geplanten Mahlzeiten dieser Woche verbrauchen. Das heißt nicht, dass du es schon gegessen hast, und der Rest jeder Packung bleibt in deiner Küche.",
                explanation: "Zusammen mit dem, was in deiner Küche bleibt, erklärt er deinen ganzen Einkauf: Alles, was du kaufst, wird entweder von den Mahlzeiten dieser Woche verbraucht oder bleibt für später übrig. Gezählt wird nur, was du bei diesem Einkauf kaufst; was du schon zu Hause hattest, gehört zum voraussichtlichen Wochendurchschnitt.",
                example: "61 € dieses Einkaufs von 96 € sind Lebensmittelverbrauch dieser Woche; der Rest bleibt in deiner Küche.",
            },
        },
    },
    {
        id: "cheapest-valid-week",
        category: "budget-shopping",
        kind: "estimate",
        text: {
            en: {
                name: "Lowest-cost plan we found",
                shortDefinition: "The cheapest week BiteBalance could build that still meets your nutrition and food rules.",
                explanation: "When a week can't fit under your maximum checkout, BiteBalance shows you the cheapest week it could build so you can see how far off it is. Your allergies, food rules and nutrition targets are never loosened to get the price down.",
                example: "\"The lowest-cost plan we found costs €96 at the till — €6 over your €90 maximum.\"",
            },
            de: {
                name: "Günstigster gefundener Plan",
                shortDefinition: "Die günstigste Woche, die BiteBalance bauen konnte und die deine Ernährungs- und Lebensmittelregeln einhält.",
                explanation: "Wenn eine Woche nicht unter deinen maximalen Einkaufsbetrag passt, zeigt dir BiteBalance die günstigste Woche, die es bauen konnte — so siehst du, wie weit es fehlt. Deine Allergien, Lebensmittelregeln und Ernährungsziele werden nie gelockert, um den Preis zu drücken.",
                example: "„Der günstigste gefundene Plan kostet 96 € an der Kasse — 6 € über deinem Maximum von 90 €.“",
            },
        },
    },
    {
        id: "items-without-reliable-price",
        category: "budget-shopping",
        kind: "info",
        text: {
            en: {
                name: "Items without a reliable price",
                shortDefinition: "Things on your list BiteBalance couldn't price — for example items you typed in yourself.",
                explanation: "BiteBalance can't give you a trustworthy total until every item you need has a reliable price. It never treats a missing price as €0 and never guesses, so while any item is unpriced you'll see \"Can't confirm yet\" instead of a number that might be wrong.",
                example: "\"2 items without a reliable price — we can't confirm this shop stays under €90.\"",
            },
            de: {
                name: "Artikel ohne verlässlichen Preis",
                shortDefinition: "Dinge auf deiner Liste, die BiteBalance nicht bepreisen konnte — zum Beispiel selbst eingetragene Artikel.",
                explanation: "BiteBalance kann dir erst eine verlässliche Summe nennen, wenn jeder benötigte Artikel einen verlässlichen Preis hat. Ein fehlender Preis wird nie als 0 € gezählt und nie geraten — solange ein Artikel unbepreist ist, siehst du „Noch nicht bestätigbar“ statt einer Zahl, die falsch sein könnte.",
                example: "„2 Artikel ohne verlässlichen Preis — wir können nicht bestätigen, dass dieser Einkauf unter 90 € bleibt.“",
            },
        },
    },
];
/** Status words (exact — no tolerance band, owner decision D-3). */
export const GLOSSARY_STATUS = {
    en: {
        goal: { at_or_below_goal: "On track", above_goal: "Above goal", unavailable: "Not available" },
        checkout: { no_max: "No maximum set", within_max: "Within your maximum", exceeds_max: "Over your maximum", unverifiable: "Can't confirm yet" },
        aboveGoalBy: "{{amount}} above your goal",
        belowGoalBy: "{{amount}} below your goal",
        atGoal: "Exactly at your goal",
        overMaxBy: "{{amount}} over your maximum",
        underMaxBy: "{{amount}} under your maximum",
    },
    de: {
        goal: { at_or_below_goal: "Im Ziel", above_goal: "Über dem Ziel", unavailable: "Nicht verfügbar" },
        checkout: { no_max: "Kein Maximum gesetzt", within_max: "Unter deinem Maximum", exceeds_max: "Über deinem Maximum", unverifiable: "Noch nicht bestätigbar" },
        aboveGoalBy: "{{amount}} über deinem Ziel",
        belowGoalBy: "{{amount}} unter deinem Ziel",
        atGoal: "Genau im Ziel",
        overMaxBy: "{{amount}} über deinem Maximum",
        underMaxBy: "{{amount}} unter deinem Maximum",
    },
};
export function getGlossaryTerm(id) {
    const term = GLOSSARY_TERMS.find((t) => t.id === id);
    if (!term)
        throw new Error(`unknown glossary term: ${id}`);
    return term;
}
export function glossaryText(id, locale) {
    return getGlossaryTerm(id).text[locale];
}
export function glossaryTermsInCategory(category) {
    return GLOSSARY_TERMS.filter((t) => t.category === category);
}
export function isGlossaryTermId(value) {
    return typeof value === "string" && GLOSSARY_TERMS.some((t) => t.id === value);
}
/** Stable deep links — the URL contract for "Learn more". Ids live in the
 * URL only; they are never rendered as visible text. */
export const GLOSSARY_LINKS = {
    webPath: "/guides/glossary",
    web(id) {
        return `/guides/glossary#${id}`;
    },
    mobileRoute: "/more/guide-center/glossary",
    mobile(id) {
        return `/more/guide-center/glossary?term=${id}`;
    },
};
